// BARIS BUNDEL di pesanan & nota (mig 8040) — helper MURNI, tanpa I/O.
//
// Rumus harga bundel TIDAK di sini (`src/lib/bundle.ts`); berkas ini hanya
// menjawab pertanyaan TAMPILAN & GERBANG atas baris `order_items` yang sudah
// ber-jejak bundel (`order_bundle_id`):
//   • label baris ("Bundel: Couple Raya Aira");
//   • K4 — nota PEMBELI untuk bundel Ready Stock dibuat UTUH: mencentang satu
//     baris mencentang semua baris belum-lunas pembelian yang sama, melepas
//     satu melepas kelompoknya (cermin `bundle_assert_invoice_whole`);
//   • "Batalkan Bundel" pembeli (cermin gerbang `cancel_my_open_po_bundle`).
// Server tetap yang mengikat; cermin ini mencegah tombol yang pasti ditolak.

/** Hint di layar Buat Nota / Nota + Kirim pembeli (K4). */
export const BUNDLE_TAGIHAN_WHOLE_HINT = "Bundling ditagihkan utuh";

/**
 * Hadiah pembelian di layar nota (H27, mig 8780): syarat boleh ditagih per
 * produk; HADIAH ikut nota yang memuat semua syaratnya yang belum lunas
 * (biasanya nota syarat terakhir). Dibaca pembeli & admin — tanpa kata ganti.
 */
export const GIFT_TAGIHAN_WHOLE_HINT = "ikut tagihan syarat terakhir";

/** Petunjuk baris pembelian berhadiah di layar nota: hanya baris HADIAH yang berpetunjuk. */
export function giftTagihanHint(giftRole: string | null | undefined): string | null {
  return giftRole === "hadiah" ? GIFT_TAGIHAN_WHOLE_HINT : null;
}

/** Alasan tombol "+" Ubah Pcs admin mati pada baris bundel (cermin mig 8040 §7a). */
export const BUNDLE_RAISE_BLOCK_TEXT = "Baris bundling — tambahkan sebagai barang biasa.";

/** Peringatan saat admin mengurangi / membatalkan baris bundel (§6.6). */
export const BUNDLE_CANCEL_WARNING = "Baris lain di bundling ini tetap harga bundling.";

/** Angka kolom numerik PostgREST (number / string) → number; kosong/rusak → null. */
function numericOrNull(v: number | string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Bagian potongan BUNDEL per pcs sebuah baris pesanan (mig 8040), atau `null`
 * bila baris tak ber-jejak bundel.
 *
 * Kolomnya (semua per pcs, termasuk tambahan):
 *   • `bundle_base_unit_price` = harga TERPISAH TANPA voucher;
 *   • `bundle_base_unit_price_after_voucher` = harga terpisah SESUDAH voucher —
 *     keluaran mesin voucher tepat sebelum langkah bundel K5; NULL bila baris
 *     tak ber-voucher;
 *   • `unit_price` = harga bayar (sesudah voucher & bundel).
 *
 * Aturannya:
 *   • baris ber-voucher (`voucher_discount_per_pcs` terisi) dengan potret
 *     pasca-voucher → `afterVoucher − unit`. Bukan `base − unit − voucher/pcs`:
 *     voucher per pcs dihitung atas harga UMUM, jadi bila ada tier persen
 *     (level/promo) DI BAWAH voucher, pengurangan itu salah mengatribusi
 *     (umum 100.000, level 20 %, voucher 10 %: base 80.000 tapi sesudah
 *     voucher 72.000, bukan 70.000);
 *   • baris tanpa voucher (termasuk sesudah admin Lepas Voucher, yang
 *     mengosongkan `voucher_discount_per_pcs`) → `base − unit`;
 *   • jaring pengaman: voucher terisi tapi potret pasca-voucher absen (payload
 *     yang tak membawa kolomnya) → `base − unit − voucher/pcs` seperti dulu,
 *     supaya potongan voucher tak pernah ikut terhitung sebagai bundel.
 * Dibulatkan SEKALI (`Math.round`, aturan suku voucher `lineDiscountSplit`);
 * tak pernah negatif.
 */
export function orderItemBundleDiscountPerPcs(row: {
  bundleBaseUnitPrice: number | string | null | undefined;
  /** `order_items.bundle_base_unit_price_after_voucher` — absen = tak dibawa payload. */
  bundleBaseUnitPriceAfterVoucher?: number | string | null;
  unitPrice: number | string | null | undefined;
  voucherDiscountPerPcs?: number | string | null;
  /**
   * Harga satuan yang DITAGIH baris nota (`amount / qty`). Lebih tinggi dari
   * `unit_price` pesanan = Edit Nota menaikkan harga (RENCANA §6.6: menagih
   * harga satuan sisa bundel) → bagian bundel menyusut, bukan melabeli diskon
   * level sebagai "Bundel". Lebih rendah (potongan tambahan di nota) tak
   * menambah bagian bundel. Absen = harga pesanan.
   */
  billedUnitPrice?: number | null;
}): number | null {
  const base = numericOrNull(row.bundleBaseUnitPrice);
  const orderUnit = numericOrNull(row.unitPrice);
  if (base === null || orderUnit === null) return null;
  const billed = row.billedUnitPrice;
  const unit =
    typeof billed === "number" && Number.isFinite(billed) ? Math.max(orderUnit, billed) : orderUnit;
  const rawVoucher = numericOrNull(row.voucherDiscountPerPcs ?? null);
  const hasVoucher = rawVoucher !== null && rawVoucher > 0;
  if (!hasVoucher) return Math.max(0, Math.round(base - unit));
  const afterVoucher = numericOrNull(row.bundleBaseUnitPriceAfterVoucher ?? null);
  if (afterVoucher !== null) return Math.max(0, Math.round(afterVoucher - unit));
  return Math.max(0, Math.round(base - unit) - Math.round(rawVoucher));
}

/** Label baris bundel di keranjang pesanan & nota. Nama tak terbaca → "Bundel". */
export function bundleRowLabel(name: string | null | undefined): string {
  const n = (name ?? "").trim();
  return n ? `Bundling: ${n}` : "Bundling";
}

/** Baris HIDUP (bukan batal) yang ber-jejak bundel. */
export function isLiveBundleRow(row: {
  orderBundleId?: string | null;
  keepStatus: string | null;
}): boolean {
  return Boolean((row.orderBundleId ?? "").trim()) && row.keepStatus !== "cancelled";
}

// ─── K4: nota pembeli untuk bundel Ready Stock dibuat utuh ───────────────────

export type BundleTagihanRow = {
  itemId: string;
  orderBundleId?: string | null;
  productType: string;
  /**
   * Pembelian BERHADIAH baris ini (mig 8680). Absen = baris biasa — pemakai
   * lama yang tak membawanya berperilaku persis seperti dulu.
   */
  orderGiftId?: string | null;
  /**
   * Peran baris berhadiah: `syarat` | `hadiah` (H27, mig 8780). Absen pada
   * salah satu baris pembelian = kelompok utuh seperti Tahap 1 (gagal-tutup).
   */
  giftRole?: string | null;
};

/**
 * Baris yang dinotakan UTUH pcs bebasnya dan ikut kelompoknya:
 *   • baris bundel Ready Stock (K4). PO sengaja bebas — kesiapan antar-produk
 *     berbeda dan DP saat PO tutup sudah mengikat komitmen (RENCANA §6.5);
 *   • baris syarat & hadiah pembelian berhadiah, PO maupun Ready Stock (mig
 *     8680). Sejak H27 (mig 8780) syarat boleh ditagih per produk; HADIAH
 *     hanya di nota yang memuat SEMUA syarat yang belum lunas — trigger
 *     `HADIAH_NOTA_UTUH` menolak selainnya.
 */
export function isWholeTagihanBundleRow(row: BundleTagihanRow): boolean {
  if ((row.orderGiftId ?? "").trim()) return true;
  return Boolean((row.orderBundleId ?? "").trim()) && row.productType === "ready_stock";
}

/** Baris satu pembelian berhadiah di daftar layar, dipisah peran; null = peran tak lengkap (kelompok utuh). */
function giftFamily(
  rows: readonly BundleTagihanRow[],
  giftId: string
): { syarat: BundleTagihanRow[]; hadiah: BundleTagihanRow[] } | null {
  const family = rows.filter((r) => (r.orderGiftId ?? "").trim() === giftId);
  if (!family.every((r) => r.giftRole === "syarat" || r.giftRole === "hadiah")) return null;
  return {
    syarat: family.filter((r) => r.giftRole === "syarat"),
    hadiah: family.filter((r) => r.giftRole === "hadiah")
  };
}

/**
 * Id baris yang ikut TERLEPAS bersama `itemId` di antara `rows` (daftar baris
 * YANG BISA DINOTAKAN di layar itu — pcs bebas > 0):
 *   • bundel Ready Stock → seluruh kelompoknya;
 *   • syarat berhadiah → dirinya + baris hadiah pembelian itu (H27: hadiah
 *     butuh semua syarat yang belum lunas); hadiah → dirinya;
 *   • pembelian berhadiah berperan tak lengkap → seluruh pembelian (Tahap 1);
 *   • baris biasa → dirinya.
 */
export function bundleTagihanGroup(rows: readonly BundleTagihanRow[], itemId: string): string[] {
  const row = rows.find((r) => r.itemId === itemId);
  if (!row || !isWholeTagihanBundleRow(row)) return [itemId];
  const giftId = (row.orderGiftId ?? "").trim();
  if (giftId) {
    const fam = giftFamily(rows, giftId);
    if (!fam) return rows.filter((r) => (r.orderGiftId ?? "").trim() === giftId).map((r) => r.itemId);
    return row.giftRole === "hadiah" ? [itemId] : [itemId, ...fam.hadiah.map((r) => r.itemId)];
  }
  return rows
    .filter((r) => isWholeTagihanBundleRow(r) && !(r.orderGiftId ?? "").trim() && r.orderBundleId === row.orderBundleId)
    .map((r) => r.itemId);
}

/**
 * Pilihan `picked` dilengkapi (H27): hadiah yang tercentang menarik semua
 * syaratnya di daftar; semua syarat tercentang menarik hadiahnya. Bundel &
 * pembelian berperan tak lengkap tak tersentuh di sini.
 */
function closeGiftPicks(rows: readonly BundleTagihanRow[], picked: Set<string>): Set<string> {
  const giftIds = new Set(
    rows.filter((r) => picked.has(r.itemId) && (r.orderGiftId ?? "").trim()).map((r) => (r.orderGiftId ?? "").trim())
  );
  for (const giftId of giftIds) {
    const fam = giftFamily(rows, giftId);
    if (!fam) continue;
    if (fam.hadiah.some((r) => picked.has(r.itemId))) for (const r of fam.syarat) picked.add(r.itemId);
    if (fam.syarat.length && fam.syarat.every((r) => picked.has(r.itemId))) {
      for (const r of fam.hadiah) picked.add(r.itemId);
    }
  }
  return picked;
}

/**
 * Centang/lepas satu baris. Bundel Ready Stock membawa kelompoknya. Berhadiah
 * (H27): mencentang hadiah ikut mencentang semua syarat yang belum lunas,
 * mencentang syarat terakhir ikut mencentang hadiahnya, melepas satu syarat
 * ikut melepas hadiahnya.
 */
export function toggleBundleTagihanPick(
  rows: readonly BundleTagihanRow[],
  selected: ReadonlySet<string>,
  itemId: string
): Set<string> {
  const next = new Set(selected);
  if (selected.has(itemId)) {
    for (const id of bundleTagihanGroup(rows, itemId)) next.delete(id);
    return next;
  }
  // Berhadiah berperan lengkap: hanya dirinya, lalu dilengkapi H27; selain itu kelompoknya.
  const row = rows.find((r) => r.itemId === itemId);
  const giftId = (row?.orderGiftId ?? "").trim();
  for (const id of giftId && giftFamily(rows, giftId) ? [itemId] : bundleTagihanGroup(rows, itemId)) next.add(id);
  return closeGiftPicks(rows, next);
}

/**
 * Pra-centang (hand-off keranjang / tautan): setiap baris bundel Ready Stock
 * yang tercentang menarik kelompoknya, dan pilihan berhadiah dilengkapi (H27),
 * supaya layar tak pernah mulai dari pilihan yang pasti ditolak `request_invoice`.
 */
export function expandBundleTagihanPicks(
  rows: readonly BundleTagihanRow[],
  ids: Iterable<string>
): Set<string> {
  const out = new Set<string>();
  for (const id of ids) {
    const row = rows.find((r) => r.itemId === id);
    // Syarat berhadiah tak menarik hadiahnya kecuali semua syaratnya ikut (closeGiftPicks).
    if (row && (row.orderGiftId ?? "").trim() && row.giftRole === "syarat" && giftFamily(rows, (row.orderGiftId ?? "").trim())) {
      out.add(id);
      continue;
    }
    for (const g of bundleTagihanGroup(rows, id)) out.add(g);
  }
  return closeGiftPicks(rows, out);
}

// ─── K4 sisi ADMIN: peringatan nota yang memotong bundel (bughunt G8-28) ─────
//
// `admin_create_invoice_internal` sengaja TIDAK memanggil
// `bundle_assert_invoice_whole`, jadi remnya hanya di layar. Sejak G9-15
// (putusan pemilik 2026-10-22: "harus utuh") CENTANG admin membawa kelompok
// utuh (`toggleBundleTagihanPick` / `toggleInvoicePick` / scan
// `invoiceScanBilledCounts`); peringatan ini tinggal menangkap qty satu baris
// yang dikurangi di Buat Nota keranjang. Tanpa rem itu pembeli bisa mendapat
// satu komponen berharga alokasi bundling, keep komponen lain hangus, dan
// toko menanggung selisihnya tanpa keputusan sadar.

/** Satu baris HIDUP pembelian bundel yang dimuat layar Buat Nota admin. */
export type BundleTagihanCutRow = BundleTagihanRow & {
  bundleName?: string | null;
  keepStatus: string | null;
  /** Pcs bebas baris ini SEBELUM nota ini (cermin `order_item_free_pcs`). */
  freePcs: number;
};

export type PartialBundleTagihanGroup = {
  orderBundleId: string;
  bundleName: string | null;
  /** Baris kelompok yang masih menyisakan pcs bebas sesudah nota ini. */
  leftItemIds: string[];
  /** Σ pcs bebas yang tertinggal di luar nota ini. */
  leftPcs: number;
};

/**
 * Pembelian bundel Ready Stock yang DIPOTONG pilihan nota ini — cermin syarat
 * `bundle_assert_invoice_whole` (yang mengikat nota PEMBELI): pembelian yang
 * punya baris di nota, tetapi sesudah nota terbit masih ada baris
 * `unpaid_keep` ber-pcs bebas (baris yang tak dicentang, atau dicentang
 * dengan qty kurang dari pcs bebasnya). PO bebas (§6.5) → tak pernah masuk.
 *
 * `rows` = baris HIDUP bundel yang dimuat layar — bukan hanya yang bisa
 * dinotakan: baris paid_keep / hold / pcs bebas 0 ikut tanpa memicu apa pun,
 * persis sisi SQL. `selected` = itemId → qty dinotakan (qty ≤ 0 = tak ikut).
 * Urutan = kemunculan pertama pembelian di `rows`.
 */
export function partialBundleTagihanGroups(
  rows: readonly BundleTagihanCutRow[],
  selected: ReadonlyMap<string, number>
): PartialBundleTagihanGroup[] {
  const pickedQty = (id: string) => {
    const q = selected.get(id);
    return typeof q === "number" && Number.isFinite(q) && q > 0 ? Math.floor(q) : 0;
  };
  const byBundle = new Map<string, BundleTagihanCutRow[]>();
  for (const r of rows) {
    if (!isWholeTagihanBundleRow(r)) continue;
    const id = (r.orderBundleId ?? "").trim();
    const list = byBundle.get(id) ?? [];
    list.push(r);
    byBundle.set(id, list);
  }
  const out: PartialBundleTagihanGroup[] = [];
  for (const [orderBundleId, list] of byBundle) {
    if (!list.some((r) => pickedQty(r.itemId) > 0)) continue;
    const leftItemIds: string[] = [];
    let leftPcs = 0;
    for (const r of list) {
      if (r.keepStatus !== "unpaid_keep") continue;
      const free = Number.isFinite(r.freePcs) ? Math.max(0, Math.floor(r.freePcs)) : 0;
      const left = free - Math.min(free, pickedQty(r.itemId));
      if (left > 0) {
        leftItemIds.push(r.itemId);
        leftPcs += left;
      }
    }
    if (!leftItemIds.length) continue;
    const name = list.map((r) => (r.bundleName ?? "").trim()).find(Boolean) ?? null;
    out.push({ orderBundleId, bundleName: name, leftItemIds, leftPcs });
  }
  return out;
}

/** Kalimat peringatan satu pembelian bundel yang terpotong (admin, tanpa kata ganti). */
export function partialBundleTagihanWarning(group: Pick<PartialBundleTagihanGroup, "bundleName" | "leftPcs">): string {
  const name = (group.bundleName ?? "").trim();
  return `Bundling ${name ? `"${name}"` : "ini"} tidak ditagihkan utuh: ${group.leftPcs} pcs lain belum masuk tagihan ini. Harganya tetap harga bundling, dan keep-nya bisa hangus bila tak ditagihkan.`;
}

// ─── Batalkan Bundel (pembeli, Pre-Order) ────────────────────────────────────

export type MyBundleRow = {
  orderBundleId?: string | null;
  productType: string;
  keepStatus: string | null;
  paidQty: number;
  inActiveInvoice: boolean;
  /** `products.status` — wajib "OPEN PO". */
  productStatus: string;
  /** `orders.status` — wajib Menunggu / Diproses. */
  orderStatus: string;
  /** `order_items.item_status` — null = tak terbaca (dibiarkan lewat). */
  itemStatus?: string | null;
  /** Status nota DP pesanan ini (mig 072). */
  dpInvoiceStatus?: string | null;
};

/**
 * Cermin `order_dp_invoice_locked` sejauh yang terbaca klien — idiom
 * `dpInvoiceLocksEdit` (adminCart.ts): nota DP menunggu ACC / tersentuh uang.
 */
const DP_LOCKING_STATUSES = new Set(["pending_acc", "partial", "paid"]);

/**
 * Tombol "Batalkan Bundel" pembeli — cermin gerbang `cancel_my_open_po_bundle`
 * (dan `canEditOrder`): SETIAP baris hidup pembelian itu masih PO OPEN, pesanan
 * Menunggu/Diproses, belum di-keep, belum dibayar, belum masuk nota aktif,
 * belum masuk produksi, dan nota DP pesanannya tak terkunci. `rows` = semua baris pembelian yang diketahui layar.
 * Tanpa baris hidup = sudah batal → tombol tak ditampilkan.
 */
export function canCancelMyPoBundle(rows: readonly MyBundleRow[]): boolean {
  const live = rows.filter(isLiveBundleRow);
  if (!live.length) return false;
  return live.every(
    (r) =>
      r.productType === "preorder" &&
      r.keepStatus == null &&
      r.paidQty <= 0 &&
      !r.inActiveInvoice &&
      r.productStatus === "OPEN PO" &&
      (r.orderStatus === "Menunggu" || r.orderStatus === "Diproses") &&
      (r.itemStatus == null || r.itemStatus === "Menunggu") &&
      !DP_LOCKING_STATUSES.has(r.dpInvoiceStatus ?? "")
  );
}

/**
 * Kalimat server `cancel_my_open_po_bundle` (detail `VOUCHER_EDIT:shared`) —
 * dipakai juga sebagai keterangan pengganti tombol, supaya layar dan
 * penolakan server tak pernah berbeda kata.
 */
export const BUNDLE_CANCEL_VOUCHER_SHARED_TEXT =
  "Voucher bundling ini dipakai bersama barang lain, jadi potongannya tidak bisa dihitung ulang otomatis. Hubungi admin untuk membatalkan bundling ini.";

export type MyBundleVoucherRow = {
  orderBundleId?: string | null;
  keepStatus: string | null;
  /** `order_items.voucher_usage_id` — absen/null = baris tanpa voucher. */
  voucherUsageId?: string | null;
  /** `orders.status` — baris pesanan Batal tak dihitung (cermin SQL). */
  orderStatus?: string | null;
};

/**
 * Cermin gerbang `VOUCHER_EDIT:shared` `cancel_my_open_po_bundle`: pemakaian
 * voucher baris HIDUP pembelian ini juga menutup baris hidup LAIN (di luar
 * pembelian ini, pesanannya tak Batal). `rows` = semua baris pembeli yang
 * dimuat layar — baris yang tak dimuat tak terlihat, jadi server tetap yang
 * mengikat (kalimatnya sama, lihat `BUNDLE_CANCEL_VOUCHER_SHARED_TEXT`).
 */
export function myBundleVoucherShared(
  orderBundleId: string,
  rows: readonly MyBundleVoucherRow[]
): boolean {
  const id = orderBundleId.trim();
  if (!id) return false;
  const live = (r: MyBundleVoucherRow) => r.keepStatus !== "cancelled";
  const usages = new Set(
    rows
      .filter((r) => (r.orderBundleId ?? "").trim() === id && live(r) && r.voucherUsageId)
      .map((r) => r.voucherUsageId as string)
  );
  if (!usages.size) return false;
  return rows.some(
    (r) =>
      (r.orderBundleId ?? "").trim() !== id &&
      live(r) &&
      r.orderStatus !== "Batal" &&
      Boolean(r.voucherUsageId) &&
      usages.has(r.voucherUsageId as string)
  );
}

/**
 * Keadaan tombol "Batalkan Bundel" satu pembelian:
 *   • `available` — tombol tampil;
 *   • `voucher_shared` — lolos gerbang lain tapi vouchernya dipakai bersama
 *     barang lain → tombol diganti keterangan (hubungi admin);
 *   • `unavailable` — tak ada yang ditawarkan (sudah diproses / sudah batal).
 * `rows` = SEMUA baris pembeli yang dimuat layar (bukan hanya pembelian ini).
 */
export type MyBundleCancelState = "available" | "voucher_shared" | "unavailable";

export function myPoBundleCancelState(
  orderBundleId: string,
  rows: readonly (MyBundleRow & MyBundleVoucherRow)[]
): MyBundleCancelState {
  const id = orderBundleId.trim();
  const own = rows.filter((r) => (r.orderBundleId ?? "").trim() === id);
  if (!id || !canCancelMyPoBundle(own)) return "unavailable";
  return myBundleVoucherShared(id, rows) ? "voucher_shared" : "available";
}

/** Kelompokkan baris per pembelian bundel (urutan kemunculan pertama). */
export function groupRowsByOrderBundle<T extends { orderBundleId?: string | null }>(
  rows: readonly T[]
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const r of rows) {
    const id = (r.orderBundleId ?? "").trim();
    if (!id) continue;
    const list = out.get(id) ?? [];
    list.push(r);
    out.set(id, list);
  }
  return out;
}

// ─── Cancel admin atas PERMINTAAN PEMBELI (mig 8040) ─────────────────────────

/**
 * Keadaan baris yang BENAR-BENAR dibatalkan mesin `admin_cancel_order_items`
 * (`v_eligible`): belum di-keep, atau keep/hold yang belum diminta kirim. Baris
 * diminta kirim / terkirim dilewati mesin tanpa galat — tak boleh dihitung
 * "ikut batal" (cermin `canCancelItem` adminCart.ts).
 */
function cancelEngineTakesRow(keepStatus: string | null | undefined): boolean {
  return (
    keepStatus == null ||
    keepStatus === "unpaid_keep" ||
    keepStatus === "paid_keep" ||
    keepStatus === "hold"
  );
}

/** Satu baris HIDUP (belum batal) pembelian bundel — bahan cermin `BUNDEL_UTUH`. */
export type BundleLiveSibling = {
  itemId: string;
  qty: number;
  /** `order_items.keep_status` — diminta kirim / terkirim membuat pembatalan pembeli mustahil. */
  keepStatus?: string | null;
};

/**
 * Baris HIDUP per pembelian bundel (`order_bundle_id`) dari SELURUH baris
 * pesanan yang dimuat — dipanggil SEBELUM penyaringan kolom keranjang
 * (`cartItemSubTab` membuang baris diminta kirim / terkirim). Persis sisi
 * "hidup" gerbang SQL `BUNDEL_UTUH`: setiap baris yang belum batal, apa pun
 * keep-nya dan apa pun status pesanannya. Baris tanpa jejak bundel dilewati.
 */
export function collectBundleLiveRows(
  rows: readonly {
    itemId: string;
    orderBundleId?: string | null;
    qty: number;
    keepStatus: string | null;
  }[]
): Map<string, BundleLiveSibling[]> {
  const out = new Map<string, BundleLiveSibling[]>();
  for (const r of rows) {
    const id = (r.orderBundleId ?? "").trim();
    if (!id || r.keepStatus === "cancelled") continue;
    const list = out.get(id) ?? [];
    list.push({ itemId: r.itemId, qty: r.qty, keepStatus: r.keepStatus });
    out.set(id, list);
  }
  return out;
}

/** Diminta kirim / terkirim: baris hidup yang TAK PERNAH diambil mesin batal. */
function inShipmentKeep(keepStatus: string | null | undefined): boolean {
  return keepStatus === "requested_shipment" || keepStatus === "shipped";
}

/**
 * Cermin gerbang `admin_cancel_order_items_by` (detail `BUNDEL_UTUH`): bila
 * pemicunya PEMBELI, pembatalan wajib mencakup SELURUH pcs hidup satu
 * pembelian bundel — pembeli tak boleh memecah bundel lewat admin. "Keputusan
 * admin" bebas (toko yang menanggung selisihnya, RENCANA §6.6).
 *
 * Persis rumus SQL-nya: Σ qty baris HIDUP pembelian itu (semua yang belum
 * batal, termasuk yang sudah diminta kirim / terkirim) harus sama dengan
 * Σ min(qty baris, qty batal) baris TERPILIH yang memang diambil mesin batal
 * (`keepStatus` null / unpaid_keep / paid_keep / hold).
 *
 * Akibatnya: pembelian yang punya baris hidup diminta kirim / terkirim TAK
 * PERNAH lolos (baris itu dihitung hidup tapi tak bisa ikut batal) — untuk itu
 * kalimatnya sendiri, bukan "pilih semua barangnya" yang mustahil dituruti.
 *
 * `lines` = baris yang dipilih + qty batalnya; `siblings` = SEMUA baris HIDUP
 * pembelian itu (`collectBundleLiveRows` atas seluruh baris pesanan yang
 * dimuat, termasuk yang tak tampil di keranjang). Kalimat umumnya = kalimat
 * server. `null` = tak ada masalah (atau tak ada baris bundel).
 */
export function buyerCancelBundleIssue(
  lines: readonly {
    itemId: string;
    orderBundleId?: string | null;
    bundleName?: string | null;
    /** Isi baris saat ini (`order_items.qty`). */
    qty: number;
    keepStatus: string | null;
    cancelQty: number;
  }[],
  siblings: ReadonlyMap<string, readonly BundleLiveSibling[]>
): string | null {
  const seen = new Set<string>();
  for (const l of lines) {
    const id = (l.orderBundleId ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const live = siblings.get(id) ?? [];
    const name = (l.bundleName ?? "").trim();
    if (live.some((r) => r.qty > 0 && inShipmentKeep(r.keepStatus))) {
      return `Sebagian barang bundling${
        name ? ` "${name}"` : ""
      } sudah diminta kirim / dikirim — pembatalan atas permintaan pembeli tidak bisa; pilih "Keputusan admin" bila toko menanggung selisihnya.`;
    }
    const liveQty = live.reduce((sum, r) => sum + Math.max(0, r.qty), 0);
    const pickedQty = lines
      .filter((x) => (x.orderBundleId ?? "").trim() === id && cancelEngineTakesRow(x.keepStatus))
      .reduce((sum, x) => sum + Math.max(0, Math.min(x.qty, x.cancelQty)), 0);
    if (pickedQty !== liveQty) {
      return `Pembatalan atas permintaan pembeli harus mencakup seluruh bundling${
        name ? ` "${name}"` : ""
      } — pilih semua barangnya, atau pilih "Keputusan admin".`;
    }
  }
  return null;
}
